import { keyframes } from '@mui/system';

export interface LokiServer {
  _id: string;
  name: string;
  moduleType: string;
  labelValueSelections?: Record<string, string[]>;
}

export interface LogEntry {
  timestamp: number;
  line: string;
  level: string;
  labels: Record<string, string>;
  id: number;
}

export interface VolumeBucket {
  timeLabel: string;
  debug: number;
  info: number;
  warn: number;
  error: number;
  critical: number;
  unknown: number;
}

export const LOGO_BASE = '/static/assets';

export const MODULE_LOGO_MAP: Record<string, string> = {
  Loki: 'loki_logo.svg',
  ClickHouse: 'clickhouse_logo.svg',
  InfluxDB: 'influxdb_logo.svg',
  MongoDB: 'mongodb_logo.svg',
};

export type LineFilterOperator =
  | 'contains'
  | 'not_contains'
  | 'contains_ci'
  | 'not_contains_ci'
  | 'regex_match'
  | 'not_regex_match';

export interface LineFilter {
  operator: LineFilterOperator;
  value: string;
}

/**
 * Master switch for the "line filter" feature (Label browser Step 3 and the
 * resulting LogQL line operators). Disabled for now because Loki is not
 * optimized for contains / does-not-contain line queries. When false, the
 * Step 3 UI is disabled and no line filter is ever applied to queries, even if
 * one was previously persisted. Flip to `true` once a different log-query
 * mechanism is implemented.
 */
export const LINE_FILTER_ENABLED = false;

export const LINE_FILTER_OPTIONS: { value: LineFilterOperator; label: string }[] = [
  { value: 'contains', label: 'Line contains' },
  { value: 'not_contains', label: 'Line does not contain' },
  { value: 'contains_ci', label: 'Line contains (case insensitive)' },
  { value: 'not_contains_ci', label: 'Line does not contain (case insensitive)' },
  { value: 'regex_match', label: 'Line contains regex match' },
  { value: 'not_regex_match', label: 'Line does not contain regex match' },
];

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const QUICK_RANGES = [
  { label: 'Last 5 minutes', from: 'now-5m', to: 'now', ms: 5 * MINUTE_MS },
  { label: 'Last 15 minutes', from: 'now-15m', to: 'now', ms: 15 * MINUTE_MS },
  { label: 'Last 30 minutes', from: 'now-30m', to: 'now', ms: 30 * MINUTE_MS },
  { label: 'Last 1 hour', from: 'now-1h', to: 'now', ms: HOUR_MS },
  { label: 'Last 3 hours', from: 'now-3h', to: 'now', ms: 3 * HOUR_MS },
  { label: 'Last 6 hours', from: 'now-6h', to: 'now', ms: 6 * HOUR_MS },
  { label: 'Last 12 hours', from: 'now-12h', to: 'now', ms: 12 * HOUR_MS },
  { label: 'Last 24 hours', from: 'now-24h', to: 'now', ms: 24 * HOUR_MS },
  { label: 'Last 2 days', from: 'now-2d', to: 'now', ms: 2 * DAY_MS },
  { label: 'Last 7 days', from: 'now-7d', to: 'now', ms: 7 * DAY_MS },
  { label: 'Last 30 days', from: 'now-30d', to: 'now', ms: 30 * DAY_MS },
  { label: 'Last 90 days', from: 'now-90d', to: 'now', ms: 90 * DAY_MS },
  { label: 'Last 6 months', from: 'now-6M', to: 'now', ms: 180 * DAY_MS },
  { label: 'Last 1 year', from: 'now-1y', to: 'now', ms: 365 * DAY_MS },
  { label: 'Last 2 years', from: 'now-2y', to: 'now', ms: 730 * DAY_MS },
] as const;

// Largest selectable span for the absolute "From / To" picker. Matches the
// longest quick range (Last 2 years).
export const MAX_TIME_RANGE_MS = 730 * DAY_MS;

export type QuickRange = typeof QUICK_RANGES[number];

export const LEVEL_COLORS: Record<string, string> = {
  debug: '#3274d9',
  info: '#56a64b',
  warn: '#f2cc0c',
  warning: '#f2cc0c',
  error: '#e02f44',
  critical: '#8f3bb8',
  fatal: '#8f3bb8',
  trace: '#6ed0e0',
  unknown: '#8e8e8e',
};

export const LEVEL_KEYS = ['debug', 'info', 'warn', 'error', 'critical', 'unknown'] as const;

export const LOG_PAGE_SIZE = 1000;
export const MAX_LIVE_ENTRIES = 5000;

export const liveBlinkAnimation = keyframes`
  0%, 100% { box-shadow: 0 0 4px rgba(76, 175, 80, 0.6); }
  50% { box-shadow: 0 0 14px rgba(76, 175, 80, 1), 0 0 28px rgba(76, 175, 80, 0.5); }
`;

export const consistentSpinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const consistentSpinnerSx = {
  animation: `${consistentSpinAnimation} 0.8s linear infinite`,
  '& .MuiCircularProgress-circle': { strokeDasharray: '60, 200' },
};
